import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Order, OrderImageTypes } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { useOrderUpdateApi } from '@hero/api-hooks';
import { toast } from 'sonner';
import { ModalForm } from '@hero/web-components';
import { OrderImageUploader } from '../OrderImageUploader';
import { buildOrderImageUpdatePayload } from '../OrderImageUploader/buildOrderImageUpdatePayload';

const DEFAULT_IMAGE_TYPE: OrderImageTypes = 'other';

export interface OrderAddImagesModalProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
}

export const OrderAddImagesModal: React.FC<OrderAddImagesModalProps> = ({
  order,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('m-order');
  const [files, setFiles] = useState<File[]>([]);
  const [imageType, setImageType] = useState<OrderImageTypes>(DEFAULT_IMAGE_TYPE);
  const [podRef, setPodRef] = useState('');
  const [applyAsOrderPodRef, setApplyAsOrderPodRef] = useState(true);

  const { mutate, isPending } = useOrderUpdateApi({
    onSuccess: () => {
      toast.success(t('toast.success.images_uploaded'));
    },
    onError: (error: Error) => {
      toast.error(t('toast.error.images_upload', { message: error.message }));
    },
  });

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setImageType(DEFAULT_IMAGE_TYPE);
      setPodRef(order.podRef ?? '');
      // Auto-select: empty order POD ref → apply whatever they type as the order's podRef
      setApplyAsOrderPodRef(true);
    }
  }, [isOpen, order.podRef]);

  const handleSave = useCallback(async () => {
    if (files.length === 0) return;

    const payload = await buildOrderImageUpdatePayload({
      files,
      imageType,
      podRef,
      applyAsOrderPodRef,
    });

    mutate(
      {
        _id: getObjectId(order._id),
        ...payload,
      },
      { onSuccess: () => onClose() },
    );
  }, [files, imageType, podRef, applyAsOrderPodRef, mutate, order._id, onClose]);

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={onClose}
      title={t('modal.add_images.title')}
      description={t('modal.add_images.description')}
      onSave={handleSave}
      isLoading={isPending}
      disableSave={files.length === 0}
      saveText={
        isPending ? t('modal.add_images.saving') : t('modal.add_images.save')
      }
      size="md">
      <OrderImageUploader
        files={files}
        onFilesChange={setFiles}
        imageType={imageType}
        onImageTypeChange={setImageType}
        podRef={podRef}
        onPodRefChange={setPodRef}
        orderPodRef={order.podRef}
        applyAsOrderPodRef={applyAsOrderPodRef}
        onApplyAsOrderPodRefChange={setApplyAsOrderPodRef}
      />
    </ModalForm>
  );
};
